import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RorosCadenceService } from '@/services/RorosService';
import type { CadenceScheduleConfig, StoredParticipant } from '@/services/roros/cadence.service';
import { rorosKeys } from './keys';
import { meetingKeys } from './meetings';

const STALE_TIME = 2 * 60_000;
const GC_TIME = 5 * 60_000;

// ─── Queries ───────────────────────────────────────────────────────────────

export function useRorosCadenceSchedules() {
  return useQuery({
    queryKey: rorosKeys.cadence.schedules,
    queryFn: () => RorosCadenceService.listSchedules(),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCadenceReviews(params?: Record<string, string>) {
  return useQuery({
    queryKey: [...rorosKeys.cadence.reviews, params] as const,
    queryFn: () => RorosCadenceService.listReviews(params),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCadenceReview(id: string) {
  return useQuery({
    queryKey: rorosKeys.cadence.review(id),
    queryFn: () => RorosCadenceService.getReview(id),
    enabled: !!id,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

export function useRorosCadenceScheduleUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosCadenceService.updateSchedule(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.schedules });
      qc.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}

export function useRorosCadenceReviewCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => RorosCadenceService.createReview(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.reviews });
    },
  });
}

export function useRorosCadenceReviewUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosCadenceService.updateReview(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.reviews });
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.review(id) });
    },
  });
}

export function useRorosCadenceReviewComplete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      RorosCadenceService.completeReview(id, payload),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.reviews });
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.review(id) });
    },
  });
}

// ─── M-Rhythm-1: new schedule_config / agenda / defaults mutations ──────────

export function useRorosCadenceScheduleConfigUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, config }: { id: string; config: CadenceScheduleConfig }) =>
      RorosCadenceService.updateScheduleConfig(id, config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.schedules });
      qc.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}

export function useRorosCadenceScheduleDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: {
      id: string;
      payload: {
        default_accountable_actor_id: string | null;
        default_moderator_actor_id: string | null;
        /** Wave-3 C8: optional default participants array */
        default_participants?: StoredParticipant[];
      };
    }) =>
      RorosCadenceService.setDefaults(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rorosKeys.cadence.schedules });
      qc.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}

export function useRorosCadenceAgendaTemplate(scheduleId: string) {
  return useQuery({
    queryKey: [...rorosKeys.cadence.schedules, scheduleId, 'agenda-template'] as const,
    queryFn: () => RorosCadenceService.listAgendaTemplate(scheduleId),
    enabled: !!scheduleId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });
}

export function useRorosCadenceAgendaTemplateSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<{ title: string; accountable_actor_id?: string | null; duration_minutes?: number | null; sort_order?: number }> }) =>
      RorosCadenceService.setAgendaTemplate(id, items),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: [...rorosKeys.cadence.schedules, id, 'agenda-template'] });
      qc.invalidateQueries({ queryKey: meetingKeys.all });
    },
  });
}
